import React from "react";
import { PhoneCall, Scale, ShieldPlus } from "lucide-react";
import { PhoneButton, PHONE } from "./PhoneButton";

const agentImage =
  "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxjdXN0b21lciUyMHNlcnZpY2UlMjBhZ2VudCUyMGhlYWRzZXR8ZW58MHx8fHwxNzgyNjg0NzUwfDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  {
    n: "01",
    Icon: PhoneCall,
    title: "Call Our Licensed Agent",
    desc: "Dial (689) 228-9122 and a licensed insurance agent answers within minutes. No phone trees, no transfers.",
  },
  {
    n: "02",
    Icon: Scale,
    title: "We Compare 30+ Carriers",
    desc: "Tell us your age and budget. We instantly shop the top-rated insurers to surface your lowest rates.",
  },
  {
    n: "03",
    Icon: ShieldPlus,
    title: "Lock in Coverage Today",
    desc: "Pick the plan that fits your fixed income. Many seniors are approved on the same call — no exam.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-16 md:py-24 bg-[#f1f5f9]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Image */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-blue-100 -z-10" />
            <img
              src={agentImage}
              alt="Friendly licensed insurance agent ready to answer your call"
              data-testid="agent-image"
              loading="lazy"
              className="w-full h-[420px] md:h-[520px] object-cover rounded-[2rem] shadow-xl border border-white"
            />
          </div>
        </div>

        {/* Steps */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-green-700">
            How It Works
          </p>
          <h2
            data-testid="how-it-works-heading"
            className="font-serif-display mt-3 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-blue-900"
          >
            Three simple steps to affordable coverage.
          </h2>
          <p className="mt-5 text-xl text-slate-700 leading-relaxed max-w-2xl">
            No paperwork to fill out online. No confusing forms. Just a friendly
            conversation with a real licensed agent.
          </p>

          <ol className="mt-10 space-y-6">
            {STEPS.map(({ n, Icon, title, desc }, i) => (
              <li
                key={n}
                data-testid={`how-step-${i + 1}`}
                className="flex gap-5 md:gap-7 bg-white rounded-2xl p-6 md:p-7 border border-slate-200 shadow-sm"
              >
                <div className="flex-shrink-0 h-16 w-16 rounded-2xl bg-green-700 text-white flex flex-col items-center justify-center font-serif-display">
                  <Icon className="h-6 w-6" />
                  <span className="text-xs font-bold mt-0.5">{n}</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 leading-snug">
                    {title}
                  </h3>
                  <p className="mt-2 text-lg text-slate-700 leading-relaxed">
                    {desc}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-10">
            <PhoneButton
              size="lg"
              testId="how-it-works-call-button"
              label={`Call ${PHONE.display}`}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
